import { cn } from '@/lib/utils';
import { CarbonArcConnectCard } from '@/components/carbon-arc-connect-card';
import { useCarbonArcConnection } from '@/hooks/queries/oauth/use-carbon-arc-connection';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode } from '@/types/node-types';

// Built backend-side from the MCP service name as `<service>_mcp_tool`.
const CARBON_ARC_NODE_SUB_TYPE = 'carbon_arc_mcp_tool';

const isCarbonArcNode = (node: CustomNode): boolean =>
  node.data?.struct?.node_sub_type === CARBON_ARC_NODE_SUB_TYPE;

interface CarbonArcConnectBannerProps {
  className?: string;
}

export const CarbonArcConnectBanner = ({
  className,
}: CarbonArcConnectBannerProps) => {
  const hasCarbonArcNode = useNodeStore((state) =>
    state.nodes.some(isCarbonArcNode),
  );
  const { data: isConnected } = useCarbonArcConnection({
    enabled: hasCarbonArcNode,
  });

  // Only an explicit "not connected" prompts. While the check is in flight, or
  // when it could not be answered, saying nothing beats asking the user to
  // reconnect an account that may be connected already.
  if (!hasCarbonArcNode || isConnected !== false) return null;

  // Deliberately outside validation state, so Run stays enabled: this is a
  // connection the user lacks, not a defect in the workflow.
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-xl shadow-sm',
        'bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800',
        className,
      )}
      data-testid='carbon-arc-connect-banner'
    >
      <span className='text-sm text-amber-800 dark:text-amber-200'>
        This workflow uses Carbon Arc tools. Connect your account to run them.
      </span>
      <CarbonArcConnectCard />
    </div>
  );
};
